import React, { useMemo } from 'react'
import { Box, Card, CardActionArea, Chip, LinearProgress, Pagination, Typography } from '@mui/material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { formatDuration } from '../model/durationFormat'
import { HISTORY_PAGE_SIZE } from '../model/historyQuery'
import { containsId, itemsOf } from '../model/historyGroups'
import { historyItemOf, shownDeltaMs, STEP_CHIP, statusChipOf } from '../model/historyView'
import MiniBars from './MiniBars'
import UiMessageAlert from './UiMessageAlert'

/** «−15 min», «+5 min» or «0 min»: the change of the imputed time with its sign. */
export const signedDuration = (milliseconds, t_) => {
  const text = formatDuration(Math.abs(milliseconds), t_)
  if (milliseconds < 0) return `−${text}`
  if (milliseconds > 0) return `+${text}`
  return text
}

export const StatusChip = ({ status }) => {
  const { t_ } = useNamesTranslation()
  const chip = statusChipOf(status)
  return <Chip size='small' color={chip.color} label={t_(chip.label)} data-testid='status-chip' />
}

/** The chips of the steps of a two-step correction (RN7, M:1065): «1 · Close now» and «2 · Trim» / «2 · Void». */
export const StepChips = ({ steps }) => {
  const { t_ } = useNamesTranslation()
  return steps.map((step) => {
    const chip = STEP_CHIP[step]
    return <Chip key={step} size='small' color={chip.color} variant='outlined' label={`${chip.number} · ${t_(chip.label)}`} data-testid={`step-chip-${step}`} />
  })
}

// One element of the list (maqueta `.h-item`): status of the group (no correction id is ever shown, PO 2026-10-17), when, «worker · machine», what, the chips of the steps,
// the imputed time and progress chips (#207 §8.1), and the mini bars (RN8). A close and its trim in the page are ONE element.
const HistoryRow = ({ row, selected, disabled, onSelect }) => {
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  return (
    <Card variant='outlined' sx={{ borderColor: selected ? 'primary.main' : 'divider', boxShadow: selected ? (theme) => `0 0 0 1px ${theme.palette.primary.main}` : 'none' }}>
      <CardActionArea
        role='option' aria-selected={selected} aria-disabled={disabled} disabled={disabled} data-testid={`history-row-${row.key}`}
        onClick={() => onSelect(row.selection.id, row.selection.partnerId)} sx={{ p: 1.25, display: 'grid', gap: 0.5 }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusChip status={row.status} />
          <StepChips steps={row.steps} />
          <Typography variant='caption' color='text.secondary' sx={{ ml: 'auto' }}>{formatDateTime(row.requestedAtMs)}</Typography>
        </Box>
        <Typography variant='body2' color='text.secondary'><b>{row.worker}</b> · {row.device}</Typography>
        <Typography variant='body2'>{t_(row.what.key, row.what.params)}</Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {row.imputed.notApplicable
            ? null
            : row.imputed.available
              ? <Chip size='small' variant='outlined' color={row.imputed.deltaMs < 0 ? 'success' : 'default'} label={t_('{{delta}} imputed', { delta: signedDuration(shownDeltaMs(row.imputed), t_) })} />
              : <Chip size='small' variant='outlined' label={t_('Imputed time not available')} />}
          {row.progress.changed && (
            <Chip
              size='small' variant='outlined'
              label={row.progress.measured ? t_('Progress {{before}} → {{after}}', { before: row.progress.before, after: row.progress.after }) : t_('not measured by time')}
            />
          )}
        </Box>
        <MiniBars bars={row.miniBars} />
      </CardActionArea>
    </Card>
  )
}

/** The master list: the rows of the page, its message when the page failed, and the pager (pages of 20). */
const HistoryList = ({ list, page, selectedId, disabled = false, onSelect, onPage, onRetry }) => {
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  const rows = useMemo(
    () => (list.response && !list.message.key ? itemsOf(list.response.content).map((item) => historyItemOf(item, formatDateTime)) : []),
    [list.response, list.message.key, formatDateTime]
  )
  const totalPages = list.response ? Math.ceil(list.response.totalElements / HISTORY_PAGE_SIZE) : 0

  return (
    <Box sx={{ display: 'grid', gap: 1, alignContent: 'start', minWidth: 0 }}>
      {list.loading && <LinearProgress />}
      {list.message.key && <UiMessageAlert message={list.message} onAction={onRetry} />}
      {/* Rows as tall as their card: with the height capped, `auto` rows shrink the cards (overflow hidden) instead of scrolling. */}
      <Box role='listbox' aria-label={t_('Corrections')} sx={{ display: 'grid', gridAutoRows: 'max-content', gap: 1, maxHeight: { md: 'calc(100vh - 300px)' }, overflowY: 'auto', pr: 0.25 }}>
        {rows.map((row) => <HistoryRow key={row.key} row={row} selected={containsId(row.item, selectedId)} disabled={disabled} onSelect={onSelect} />)}
        {!list.loading && !list.message.key && list.response && rows.length === 0 && (
          <Typography color='text.secondary' sx={{ p: 2 }}>{t_('There are no corrections with these filters.')}</Typography>
        )}
      </Box>
      {totalPages > 1 && (
        <Pagination size='small' disabled={disabled} count={totalPages} page={page + 1} onChange={(_event, value) => onPage(value - 1)} />
      )}
    </Box>
  )
}

export default HistoryList
